import EditOutlined from "@mui/icons-material/EditOutlined";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { constructPatientMessage } from "../../../../api/query/messages";
import useDevMode from "../../../../utils/devmode";
import Modal from "../../../Modal";
import { PatientDetailsForm } from "../../forms/PatientDetailsForm/PatientDetailsForm";
import type { FormFields } from "../../forms/PatientDetailsForm/PatientDetailsForm.zod";
import DetailsCard from "../../ui/DetailsCard";
import {
  removeNonDigits,
  toPatientDetailsFields,
  toResponsibleDetailsFields,
} from "../../utils";

interface PatientInfoTabProps {
  patient?: PatientMessage;
  onSave: (patient: PatientMessage) => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  triggerPatientEditModal: boolean;
  closePatientEditModal: () => void;
  practices: PracticeMessage[];
  relatedProfiles?: React.ReactNode;
}

const getFullName = (p?: PatientMessage) => {
  if (!p) return "";
  const middle = p.wieldyMiddleName ? ` ${p.wieldyMiddleName}` : "";
  return `${p.wieldyFirstName || ""}${middle} ${p.wieldyLastName || ""}`.trim();
};

export function PatientInfoTab({
  patient,
  onSave,
  updateSnackBar: _updateSnackBar,
  triggerPatientEditModal,
  closePatientEditModal,
  practices,
  relatedProfiles,
}: PatientInfoTabProps) {
  const theme = useTheme();
  const devMode = useDevMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirect = searchParams.get("redirect");

  const [showEditPatientsModal, setShowEditPatientsModal] = useState(
    !!redirect && !!patient,
  );

  const [defaultResponsibleForm, setDefaultResponsibleForm] = useState(false);

  const [draftPatient, setDraftPatient] = useState<PatientMessage | undefined>(
    redirect && patient ? constructPatientMessage(patient) : undefined,
  );

  const patientDetailsFields = toPatientDetailsFields(
    constructPatientMessage(patient),
  );
  const draftPatientDetailsFields = toPatientDetailsFields(
    constructPatientMessage(draftPatient),
  );

  const responsibleDetailsFields = toResponsibleDetailsFields(
    constructPatientMessage(patient),
  );

  const draftResponsibleDetailsFields = toResponsibleDetailsFields(
    constructPatientMessage(draftPatient),
  );

  const openEditPatientModal = () => {
    setDraftPatient(constructPatientMessage(patient));
    setShowEditPatientsModal(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding openEditPatientModal would change when this runs
  useEffect(() => {
    if (triggerPatientEditModal) {
      openEditPatientModal();
      setDefaultResponsibleForm(true);
    }
  }, [triggerPatientEditModal]);

  const closeEditPatientModal = () => {
    setShowEditPatientsModal(false);
    closePatientEditModal();

    if (redirect) {
      searchParams.delete("redirect");
      const to = { pathname: redirect, search: searchParams.toString() };
      navigate(to);
    }
  };

  const onSubmit = (data: FormFields) => {
    const { isAdditionalInfoRequired, ...formFields } = data;

    if (!draftPatient) {
      return;
    }

    const updatedPatient: PatientMessage = {
      ...constructPatientMessage(draftPatient),
      ...formFields,
      ssn: data.ssn ? removeNonDigits(data.ssn) : null,
      dob: data.dob ? data.dob : draftPatient.dob,
    };

    onSave(updatedPatient);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          marginBottom: theme.spacing(2),
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            flex: relatedProfiles ? "0 0 72%" : "1",
            transition: "flex 0.2s ease",
            minWidth: 0,
          }}
        >
          <DetailsCard
            title="Patient Details"
            headerAdornment={
              <Button
                startIcon={<EditOutlined />}
                color="info"
                onClick={openEditPatientModal}
              >
                Edit
              </Button>
            }
          >
            <Box sx={{ marginBottom: theme.spacing(3) }}>
              <PatientDetailsForm
                readOnly
                onSubmit={onSubmit}
                patientDetailsFields={patientDetailsFields}
                responsibleDetailsFields={responsibleDetailsFields}
                defaultResponsibleForm={defaultResponsibleForm}
                practices={practices}
              />
            </Box>
          </DetailsCard>
        </Box>
        {relatedProfiles && (
          <Box
            sx={{
              flex: "0 0 28%",
              minWidth: 0,
              display: "flex",
              marginRight: theme.spacing(-3),
            }}
          >
            {relatedProfiles}
          </Box>
        )}
      </Box>
      {devMode && (
        <Box sx={{ marginBottom: theme.spacing(3) }}>
          <DetailsCard title="General Notes">
            <TextField label="Type a note" fullWidth multiline />
          </DetailsCard>
        </Box>
      )}
      <Modal
        title={getFullName(patient) || "Patient Details"}
        open={showEditPatientsModal}
        onClose={closeEditPatientModal}
      >
        <Grid
          container
          sx={{ height: "100%", margin: `${theme.spacing(7)} 0` }}
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={5}>
            <Typography variant="h5" sx={{ fontWeight: "600" }}>
              Patient Details
            </Typography>
            <Box sx={{ margin: `${theme.spacing(3)} 0` }}>
              <PatientDetailsForm
                onSubmit={onSubmit}
                patientDetailsFields={draftPatientDetailsFields}
                responsibleDetailsFields={draftResponsibleDetailsFields}
                defaultResponsibleForm={defaultResponsibleForm}
                practices={practices}
              />
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
}

PatientInfoTab.defaultProps = {
  patient: undefined,
};
